import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import BottleSilhouette from "./BottleSilhouette";
import GinImage from "./GinImage";
import { api } from "../lib/api";
import { getCategoryColor } from "../lib/constants";

const CATEGORY_COLOR_BEV = {
  whisky: "#8b5a2b", rum: "#a67034", tequila: "#b8a663", mezcal: "#7a5d3a",
  vodka: "#9ec0d8", liqueurs: "#b5667d", agave: "#a4b35a", aperitifs: "#c75a3a",
  wine: "#7a2434", beer: "#c79133", sake: "#d4d2c4", vermouth: "#8b3b3b", amaro: "#5e2a2a",
};

/**
 * The Liquid Atlas hero "Tonight's Pour" — bartender-curated, ephemeral, daily-changing.
 * - Reads from /api/tonight (admin override) — supports gin OR non-gin beverage.
 * - Falls back to /api/staff-pick (daily rotation) when no admin pour is set.
 * - Handwritten note + bartender attribution + optional "Why tonight?" context.
 * - Loads similar bottles in the same category.
 */
export default function TonightsPour() {
  const [pick, setPick] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/tonight").then(async (r) => {
      if (r.data?.active && (r.data.beverage || r.data.gin)) {
        const isBev = !!r.data.beverage;
        const sku = isBev ? r.data.beverage : r.data.gin;
        const note = r.data.handwritten_note || r.data.note ||
                     `${r.data.bartender || "We"} are pouring ${sku.display_name || sku.name} tonight.`;
        setPick({
          kind: isBev ? "beverage" : "gin",
          sku,
          bartender: r.data.bartender || "BENO",
          note,
          why_tonight: r.data.why_tonight,
          mood: r.data.mood,
          source: "admin_pour",
        });
        // Load 4 similar bottles (different SKU, same category)
        try {
          if (isBev) {
            const list = await api.get(`/beverages?category=${sku.category}&limit=8`);
            setSimilar((list.data.beverages || []).filter((b) => b.id !== sku.id).slice(0, 4));
          } else {
            const rel = await api.get(`/gins/${sku.id}/related`);
            setSimilar((rel.data?.similar || []).slice(0, 4).map((g) => ({ ...g, _isGin: true })));
          }
        } catch { /* similar is optional */ }
      } else {
        // Fallback: daily rotation (gin only)
        try {
          const d = await api.get("/staff-pick");
          if (d.data?.gin) {
            setPick({
              kind: "gin",
              sku: d.data.gin,
              bartender: d.data.bartender || "BENO",
              note: d.data.note || `${d.data.bartender || "We"} are pouring ${d.data.gin.name} tonight.`,
              source: "daily_rotation",
            });
            const rel = await api.get(`/gins/${d.data.gin.id}/related`);
            setSimilar((rel.data?.similar || []).slice(0, 4).map((g) => ({ ...g, _isGin: true })));
          }
        } catch { /* no pick — silently hide */ }
      }
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  if (!pick) return null;

  const sku = pick.sku;
  const isBev = pick.kind === "beverage";
  const color = isBev ? (CATEGORY_COLOR_BEV[sku.category] || "#C17F59") : getCategoryColor(sku.category);
  const detailHref = isBev ? `/spirits/item/${sku.id}` : `/gin/g/${sku.id}`;
  const displayName = isBev ? sku.display_name : sku.name;
  const isBottleOnly = isBev && sku.pricing_mode === "BOTTLE_ONLY";
  const meta = isBev
    ? (isBottleOnly
        ? `${sku.country || ""}${sku.country ? " · " : ""}Bottle only · ₹${sku.price_by_bottle || "—"}`
        : `${sku.country || ""}${sku.country ? " · " : ""}₹${sku.price_by_glass || sku.price || "—"} / ${sku.standard_pour_ml || sku.serving_size_ml || 30}ml`)
    : `${sku.country} · ₹${sku.price} / 30ml`;

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.1 }}
      className="px-5 md:px-12 py-12 md:py-16 max-w-7xl mx-auto"
      data-testid="tonights-pour"
    >
      <div className="relative bg-[#101113] border border-white/8 p-7 md:p-12 grain overflow-hidden">
        {/* Ambient color wash */}
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ background: `radial-gradient(ellipse at top right, ${color}33, transparent 65%)` }} />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full opacity-25 pointer-events-none"
             style={{ background: `radial-gradient(circle, ${color}, transparent 65%)` }} />

        <div className="relative grid grid-cols-12 gap-6 md:gap-10 items-center">
          {/* Top label */}
          <div className="col-span-12 flex items-center justify-between flex-wrap gap-3 mb-2">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="border px-3 py-1 inline-block" style={{ borderColor: color }}>
                <p className="overline text-[10px]" style={{ color }}>Tonight at BENO</p>
              </div>
              {pick.why_tonight && (
                <span className="overline text-white/45" data-testid="tonight-why">
                  · {pick.why_tonight}
                </span>
              )}
              {pick.source === "daily_rotation" && (
                <span className="overline text-white/35">· Daily rotation</span>
              )}
            </div>
            <p className="font-hand text-2xl md:text-3xl text-[#D89A74] -rotate-2" data-testid="tonight-bartender">
              ~ {pick.bartender}
            </p>
          </div>

          {/* Big bottle visual */}
          <Link to={detailHref}
                className="col-span-12 md:col-span-4 flex items-center justify-center group"
                data-testid="tonight-bottle-link">
            <div className="relative">
              <div className="absolute inset-0 -m-6 opacity-60 group-hover:opacity-90 transition-opacity"
                   style={{ background: `radial-gradient(circle, ${color}44, transparent 70%)` }} />
              {isBev
                ? <BottleSilhouette category={sku.category} size={160} />
                : (sku.image_url
                    ? <GinImage gin={sku} size={160} />
                    : <BottleSilhouette category={sku.category} size={160} />)
              }
            </div>
          </Link>

          {/* Right: handwritten note, name, CTAs */}
          <div className="col-span-12 md:col-span-8 space-y-4">
            <p className="overline text-white/45">Tonight's Pour</p>
            <h2 className="font-display text-3xl md:text-5xl leading-tight" data-testid="tonight-name">
              {displayName}
            </h2>
            <div className="flex items-center flex-wrap gap-3 text-xs">
              <span className="overline text-white/45">{meta}</span>
              {isBev && (
                <span className="text-[10px] uppercase tracking-[0.25em] px-2 py-1 border border-white/15 text-white/55">
                  {sku.category}
                </span>
              )}
              {sku.abv && <span className="overline text-white/40">{sku.abv}% ABV</span>}
            </div>

            {/* Handwritten quote */}
            <p className="font-hand text-2xl md:text-3xl text-[#D89A74] leading-snug max-w-2xl -rotate-[0.5deg]" data-testid="tonight-note">
              "{pick.note}"
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-2">
              <Link to={detailHref}
                    className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] transition-colors"
                    data-testid="tonight-view-bottle">
                <Sparkles size={12} /> View the bottle
              </Link>
              {similar.length > 0 && (
                <a href="#tonight-similar"
                   className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/65 hover:text-[#C17F59] transition-colors"
                   data-testid="tonight-similar-link">
                  Similar bottles <ArrowRight size={12} />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Similar bottles rail */}
        {similar.length > 0 && (
          <div id="tonight-similar" className="relative mt-10 pt-8 border-t border-white/8" data-testid="tonight-similar-rail">
            <p className="overline text-white/45 mb-4">More like this</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {similar.map((b) => {
                const isGin = b._isGin;
                const href = isGin ? `/gin/g/${b.id}` : `/spirits/item/${b.id}`;
                const name = isGin ? b.name : b.display_name;
                return (
                  <Link key={b.id} to={href}
                        data-testid={`tonight-similar-${b.id}`}
                        className="group flex items-center gap-3 bg-[#15171A] border border-white/8 hover:border-white/25 p-3 transition-colors">
                    <BottleSilhouette category={isGin ? b.category : (CATEGORY_COLOR_BEV[b.category] ? b.category : "classic")} size={38} />
                    <div className="min-w-0">
                      <p className="font-display text-sm leading-tight truncate">{name}</p>
                      <p className="overline text-[9px] text-white/40 mt-1">₹{b.price || "—"}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.section>
  );
}
